import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CompactUserResponse } from '@business/entities/account';
import { LabourItemResponse, OrderProductResponse, OrderVendorResponse, PricingTierResponse } from '@business/entities/order';
import { ProjectServiceResponse, ServiceClassResponse } from '@business/entities/project';
import { CommonService, ProposalService } from '@business/services';
import { Button } from '@core/components/Button';
import { FormAjaxSelect, FormCheckbox, FormInput, FormSelect } from '@core/components/form';
import { Formatter } from '@core/utils/formatter';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { Collapse, Input } from 'reactstrap';
import { AddEditTimeMaterialPageBloc } from './AddEditTimeMaterialPageBloc';
import { LineItemFormType, OrderFormType } from './TimeMaterialForm';
import { LabourTypeResponse } from '@business/entities/labour-type';
import { TableColumn } from '@modules/common/TableColumn';
import { openViewLabourTypeModal } from '../ViewLabourTypeModal';
import { OrderPackageResponse } from '@business/entities/order/OrderPackageResponse';
import './index.scss';
import { useCompany } from '@core/utils/hooks/misc';

export interface TimeMaterialLineStepProps {
  form: OrderFormType;
  packages?: OrderPackageResponse[];
  products: OrderProductResponse[];
  services: ProjectServiceResponse[];
  classes: ServiceClassResponse[];
  users: CompactUserResponse[];
  vendors: OrderVendorResponse[];
  bloc: AddEditTimeMaterialPageBloc;
  defaultTierId?: string;
  isTaxExempt?: boolean;
  labourTypes: LabourTypeResponse[];
  sectionId: number;
  hideLaborItems?: boolean;
}

export const TimeMaterialLineStep = memo((props: TimeMaterialLineStepProps) => {
  const {
    form,
    products,
    classes,
    vendors,
    bloc,
    defaultTierId,
    services,
    isTaxExempt = false,
    labourTypes,
    sectionId,
    hideLaborItems = false,
    packages = [],
  } = props;
  const tiers = useObservable(bloc.tiers$, []);
  const lineItemsAll = useObservable(form.controls.lineItems.items$, []);
  const lineItems = useMemo(
    () =>
      lineItemsAll.filter(
        l =>
          R.toNumber(l.controls.sectionId.value) === R.toNumber(sectionId) ||
          (R.toNumber(l.controls.sectionId.value) === 0 && R.toNumber(sectionId) === -1),
      ),
    [lineItemsAll, sectionId],
  );
  const formValue = useNonNilObservable(form.value$);
  const settings = useSettings();
  const isUMEnabled = settings?.tm?.isUMEnabled;
  const isClasses = settings?.invoiceClassEnabled;
  const isSalesTax = settings?.tm?.isSalesTax;
  const unitOfMeasures = settings?.tm.unitOfMeasures || [];
  const [descColumn, setDesColumn] = useState(3);
  const [selectedProducts, setSelectedProducts] = useState<OrderProductResponse[]>([]);

  const getLineLaborPrice = useCallback((items: LabourItemResponse[]) => {
    return items.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 2), 0);
  }, []);

  const getLineLaborCost = useCallback((items: LabourItemResponse[]) => {
    return items.reduce((total, item) => total + R.roundDecimal(item.cost, 2), 0);
  }, []);

  const getLineHours = useCallback((items: LabourItemResponse[]) => {
    return items.reduce((total, item) => total + R.roundDecimal(item.hour, 2), 0);
  }, []);

  const getLabourPrice = useCallback(
    (index: number) => {
      const line = formValue.lineItems[index];
      const labourItems = line && line.labourItems ? line.labourItems : [];
      if (line) {
        const totalPriceHours = labourItems.reduce((total, item) => total + R.roundDecimal(item.hour * item.price, 2), 0);
        return line.quantity * totalPriceHours;
      } else {
        return 0;
      }
    },
    [formValue.lineItems],
  );

  useEffect(() => {
    if (isUMEnabled && descColumn === 3) {
      setDesColumn(descColumn - 1);
    }
  }, [descColumn, isUMEnabled]);

  useEffect(() => {
    if (isClasses && (descColumn === 2 || descColumn === 3)) {
      setDesColumn(descColumn - 1);
    }
  }, [descColumn, isClasses]);

  const updateLineItemValue = useCallback(
    (index: number, items: LabourItemResponse[], parentIndex = -1) => {
      if (parentIndex > -1) {
        lineItems[parentIndex].controls.packageItems.items[index].patchValue({
          labourPrice: getLineLaborPrice(items),
          labourCost: getLineLaborCost(items),
          labour: getLineHours(items),
          labourItems: items.map(item => {
            return {
              labourTypeId: item.labourTypeId,
              hour: item.hour,
              price: item.price,
              cost: item.cost,
            };
          }),
        });
      } else {
        lineItems[index].patchValue({
          labourPrice: getLineLaborPrice(items),
          labourCost: getLineLaborCost(items),
          labour: getLineHours(items),
          labourItems: items.map(item => {
            return {
              labourTypeId: item.labourTypeId,
              hour: item.hour,
              price: item.price,
              cost: item.cost,
            };
          }),
        });
      }
    },
    [getLineHours, getLineLaborCost, getLineLaborPrice, lineItems],
  );

  const selectLabourPrice = useCallback(
    (id: string, index: number, parentIndex = -1) => {
      if (id == '0') {
        const data =
          parentIndex > -1
            ? lineItems[parentIndex].controls.packageItems.items[index].controls.productLabourItems.value
            : lineItems[index].controls.productLabourItems.value;
        openViewLabourTypeModal({
          typeItems: data,
          labourTypes: labourTypes,
          onReceive: (items: LabourItemResponse[]) => {
            updateLineItemValue(index, items, parentIndex);
          },
        });
      } else {
        const data =
          parentIndex > -1
            ? lineItems[parentIndex].controls.packageItems.items[index].controls.productLabourItems.value
            : lineItems[index].controls.productLabourItems.value;
        const items: LabourItemResponse[] = R.filter(data, o => o.labourTypeId == id).map(o => {
          return {
            labourTypeId: o.labourTypeId,
            hour: o.hour,
            price: o.price,
            cost: o.cost,
            labourType: R.find(labourTypes, l => l.id == o.labourTypeId),
          } as LabourItemResponse;
        });
        if (items.length) {
          updateLineItemValue(index, items, parentIndex);
        } else {
          const selectedLabourType: LabourTypeResponse | undefined = R.find(labourTypes, l => l.id == id);
          if (!selectedLabourType) return;
          const items: LabourItemResponse = {
            labourTypeId: selectedLabourType.id,
            hour: 0,
            price: selectedLabourType.price,
            cost: selectedLabourType.cost,
            labourType: selectedLabourType,
          };
          updateLineItemValue(index, [items], parentIndex);
        }
      }
    },
    [labourTypes, lineItems, updateLineItemValue],
  );

  useEffect(() => {
    if (lineItems.length === 0) {
      const ctrl = form.controls.lineItems.push();
      ctrl.patchValue({ sectionId });
    }
  }, [form, lineItems, sectionId]);

  return (
    <React.Fragment>
      <div className="d-flex align-items-center">
        <TableColumn flex={0.1}></TableColumn>
        <TableColumn flex={2} noPadding label>
          Product/Service
        </TableColumn>
        <TableColumn noPadding label style={{ marginLeft: 15 }}>
          Tier
        </TableColumn>
        <TableColumn noPadding label flex={2}>
          Description
        </TableColumn>
        <TableColumn noPadding label>
          Qty
        </TableColumn>
        <TableColumn noPadding label>
          U/M
        </TableColumn>
        <TableColumn noPadding label>
          Price
        </TableColumn>
        {!hideLaborItems && (
          <>
            <TableColumn noPadding label flex={1.5} style={{ marginLeft: 15 }}>
              Phase
            </TableColumn>
            <TableColumn noPadding label flex={0.75}>
              Labor
            </TableColumn>
            <TableColumn noPadding label flex={0.75} style={{ marginRight: 15 }}>
              Labor Price
            </TableColumn>
          </>
        )}

        {isSalesTax && (
          <TableColumn noPadding label flex={0.5}>
            Tax
          </TableColumn>
        )}
        {isClasses && (
          <TableColumn noPadding label>
            Class
          </TableColumn>
        )}
        <TableColumn noPadding label>
          Supplier
        </TableColumn>
        <TableColumn noPadding label>
          Line Total
        </TableColumn>
        <TableColumn noPadding label flex={0.5}></TableColumn>
      </div>
      {lineItems.map((inForm, index) => {
        return (
          <ColapsableLineStep
            form={form}
            inForm={inForm}
            key={inForm.uid}
            tiers={tiers}
            packages={packages}
            products={products}
            services={services}
            bloc={bloc}
            selectedProducts={selectedProducts}
            defaultTierId={defaultTierId}
            isTaxExempt={isTaxExempt}
            labourTypes={labourTypes}
            sectionId={sectionId}
            hideLaborItems={hideLaborItems}
            getLineLaborPrice={getLineLaborPrice}
            getLineLaborCost={getLineLaborCost}
            getLineHours={getLineHours}
            index={index}
            updateLineItemValue={updateLineItemValue}
            setSelectedProducts={setSelectedProducts}
            selectLabourPrice={selectLabourPrice}
            classes={classes}
            vendors={vendors}
            isPackage={inForm.controls.packageItems.items.length > 0 ? true : false}
            uid={inForm.uid}
            filteredItems={lineItems}
          />
        );
      })}
    </React.Fragment>
  );
});

export interface LineStepProps {
  form: OrderFormType;
  inForm: LineItemFormType;
  packages: OrderPackageResponse[];
  products: OrderProductResponse[];
  services: ProjectServiceResponse[];
  bloc: AddEditTimeMaterialPageBloc;
  defaultTierId?: string;
  isTaxExempt?: boolean;
  labourTypes: LabourTypeResponse[];
  sectionId: number;
  hideLaborItems?: boolean;
  tiers: PricingTierResponse[];
  selectedProducts: OrderProductResponse[];
  getLineLaborPrice: (items: LabourItemResponse[]) => number;
  getLineLaborCost: (items: LabourItemResponse[]) => number;
  getLineHours: (items: LabourItemResponse[]) => number;
  index: number;
  updateLineItemValue: (index: number, items: LabourItemResponse[], parentIndex?: number) => void;
  setSelectedProducts: (products: OrderProductResponse[]) => void;
  selectLabourPrice: (id: string, index: number, parentIndex?: number) => void;
  vendors: OrderVendorResponse[];
  classes: ServiceClassResponse[];
  isPackage?: boolean;
  parentIndex?: number;
  uid: string;
  filteredItems: LineItemFormType[];
  onOpen?: () => void;
  isOpen?: boolean;
}

export const LineStep = memo((props: LineStepProps) => {
  const {
    filteredItems,
    uid,
    form,
    inForm,
    packages,
    products,
    tiers,
    services,
    bloc,
    defaultTierId,
    isTaxExempt,
    labourTypes,
    sectionId,
    hideLaborItems,
    selectedProducts,
    getLineLaborPrice,
    getLineLaborCost,
    getLineHours,
    index,
    updateLineItemValue,
    setSelectedProducts,
    selectLabourPrice,
    vendors,
    classes,
    isPackage = false,
    parentIndex = -1,
    isOpen = false,
  } = props;
  const lineItemsAll = useObservable(form.controls.lineItems.items$, []);
  const lineItems = useMemo(
    () => lineItemsAll.filter(l => R.toNumber(l.controls.sectionId.value) === R.toNumber(sectionId)),
    [lineItemsAll, sectionId],
  );
  const settings = useSettings();
  const unitOfMeasures = settings?.tm.unitOfMeasures || [];
  const isUMEnabled = settings?.tm?.isUMEnabled;
  const isClasses = settings?.invoiceClassEnabled;
  const isSalesTax = settings?.tm?.isSalesTax;
  const company = useCompany();

  const inFormValue = useNonNilObservable(inForm.value$);
  const lineProId = R.replace(inFormValue.product, 'product-', '') || '0';
  // const lineProId = formValue.lineItems[index]?.product;
  const lineProduct = R.find(products, s => s.id == R.replace(lineProId, 'product-', ''));
  const lineService = R.find(services, s => s.id == R.replace(lineProId, 'service-', ''));
  const tierItems = useMemo(() => CommonService.selectItems(tiers, 'title', 'id', true), [tiers]);

  const lineTierIds = R.filter(lineProduct?.prices || [], p => p.price !== 0).map(p => p.tierId);
  const lineTierItems = CommonService.selectItems(
    lineProduct ? R.filter(tierItems, i => R.includes(lineTierIds, i.value)) : tierItems,
    t => {
      const tierPrice = R.find(lineProduct?.prices || [], p => p.tierId === t.value);
      return `${t.label} ${tierPrice ? ` - ${Formatter.formatCurrency(tierPrice.price, company.currencyCode)}` : ''}`;
    },
    'value',
  );

  const umItems = useMemo(
    () =>
      CommonService.selectItems(
        unitOfMeasures,
        u => u.title,
        u => u.title,
        true,
      ),
    [unitOfMeasures],
  );

  const labourTypeItems = useMemo(() => {
    return CommonService.selectItems(
      R.concat(labourTypes, [
        {
          id: '0',
          name: 'Multiple',
          price: 0,
          cost: 0,
        },
      ]),
      a => a.name,
      'id',
    );
  }, [labourTypes]);
  const classItems = useMemo(() => CommonService.selectItems(classes, 'name', 'id', true), [classes]);
  const vendorItems = useMemo(() => CommonService.selectItems(vendors, 'name', 'id', true), [vendors]);

  const totalLaborHours = useMemo(() => ProposalService.getTotalLaborHoursAddProd(inFormValue), [inFormValue]);
  const totalLaborPrice = useMemo(() => ProposalService.getLabourPriceAddProd(inFormValue), [inFormValue]);
  const totalRate = useMemo(() => ProposalService.getTotalRateAddProd(inFormValue), [inFormValue]);
  const totalQty = useMemo(() => ProposalService.getTotalQtyAddProd(inFormValue), [inFormValue]);

  const getLineTotal = useMemo(() => ProposalService.getSubTotalAddProd(inFormValue), [inFormValue]);
  //   const getLineTotal = useCallback(() => {
  //     const line = inFormValue;
  //     if (line) {
  //       debugger;
  //       if (hideLaborItems) {
  //         return line.quantity * line.rate;
  //       }
  //       return line.quantity * line.rate + totalLaborPrice;
  //     }
  //     return undefined;
  //   }, [hideLaborItems, inFormValue, totalLaborPrice]);

  const indexToModify = useMemo(() => lineItemsAll.findIndex(c => c.uid === uid), [lineItemsAll, uid]);

  const lineUM = lineProduct?.unitOfMeasure || '';
  const lineVendorId = String(lineProduct?.vendorId || '');
  const initialValue =
    lineProduct || lineService
      ? {
          label: lineProduct?.title || lineService?.title || inFormValue.lineTitle || inFormValue.description,
          value: lineProId,
        }
      : {
          label: inFormValue.product != '0' ? inFormValue.lineTitle || inFormValue.description : '',
          value: lineProId,
        };

  return (
    <div className="d-flex align-items-center">
      <TableColumn flex={0.1}>
        {!!isPackage && (
          <div className="justify-content-center align-items-center">
            <a onClick={() => props.onOpen && props.onOpen()} className="float-right">
              <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} font-size-22`}></i>
            </a>
          </div>
        )}
      </TableColumn>
      <TableColumn flex={2}>
        <FormAjaxSelect
          disabled={isPackage}
          control={inForm.controls.product}
          onChange={val => {
            const product = R.find(products, s => s.id == R.replace(val, 'product-', ''));
            const service = R.find(services, s => s.id == R.replace(val, 'service-', ''));
            const packageItem = R.find(packages, s => s.id == R.replace(val, 'package-', ''));
            if (product) {
              inForm.controls.unitOfMeasure.setValue(product.unitOfMeasure);
              inForm.controls.lineProduct.setValue(product);
              inForm.controls.cost.setValue(product.cost);
              inForm.controls.lineProduct.setValue(product);
              inForm.controls.labourPrice.setValue(getLineLaborPrice(product.labourItems));
              inForm.controls.labourCost.setValue(getLineLaborCost(product.labourItems));
              inForm.controls.labour.setValue(getLineHours(product.labourItems));
              inForm.controls.labourItems.setValue(product.labourItems);
              inForm.controls.productLabourItems.setValue(product.labourItems);
              inForm.controls.taxable.setValue(product.taxable);
              if (product.labourItems.length == 1) {
                inForm.controls.labourType.setValue(product.labourItems[0]?.labourTypeId);
                updateLineItemValue(index, product.labourItems);
              } else if (product.labourItems.length > 1) {
                inForm.controls.labourType.setValue('0');
                openViewLabourTypeModal({
                  typeItems: product.labourItems,
                  labourTypes: labourTypes,
                  onReceive: (items: LabourItemResponse[]) => {
                    updateLineItemValue(index, items);
                  },
                });
              } else {
                inForm.controls.labourType.setValue(undefined);
              }

              setSelectedProducts([...selectedProducts, product]);
              !isTaxExempt && !inForm.controls.taxable.value && inForm.controls.taxable.setValue(product.taxable);
              if (defaultTierId) {
                inForm.controls.tierId.setValue(defaultTierId);
              }
              if (product) {
                const price = R.find(product?.prices || [], s => s.tierId === defaultTierId);
                if (price) {
                  inForm.controls.rate.setValue(price.price);
                  inForm.controls.tierId.setValue(price.tierId);
                } else if ((product?.prices || []).length > 0) {
                  inForm.controls.rate.setValue(product.prices[0].price);
                  inForm.controls.tierId.setValue(product.prices[0].tierId);
                } else {
                  inForm.controls.rate.setValue(product.cost);
                }

                inForm.controls.lineTitle.setValue(product.sku);
                inForm.controls.classId.setValue(product.classId);
                //inForm.controls.rate.setValue(product.cost);
              }
              if (product.description) {
                inForm.controls.description.setValue(CommonService.stripHtml(product.description));
              }
              if (!product.description) {
                inForm.controls.description.setValue(product.title);
              }
              if (product.vendorId) {
                inForm.controls.vendorId.setValue(product.vendorId);
              }
            }
            if (packageItem) {
              let totalProducts = 0;
              let totalTaxablePros = 0;
              packageItem.lineItems.map(item => {
                const productPack = R.find(products, s => s.id == R.replace(item.productId, 'product-', ''));
                const servicePack = R.find(services, s => s.id == R.replace(item.productId, 'service-', ''));
                const ctrl = inForm.controls.packageItems.push();
                if (productPack) {
                  const price = R.find(productPack?.prices || [], s => s.tierId === defaultTierId);
                  if (price) {
                    ctrl.controls.rate.setValue(price.price);
                    ctrl.controls.tierId.setValue(price.tierId);
                  } else if ((productPack?.prices || []).length > 0) {
                    ctrl.controls.rate.setValue(productPack.prices[0].price);
                    ctrl.controls.tierId.setValue(productPack.prices[0].tierId);
                  }
                  // ctrl.controls.unitOfMeasure.setValue(item.unitOfMeasure);
                  ctrl.controls.lineProduct.setValue(productPack);
                  ctrl.controls.lineTitle.setValue(productPack.sku);
                  ctrl.controls.product.setValue(item.productId);
                  ctrl.controls.sectionId.setValue(sectionId);
                  ctrl.controls.quantity.setValue(item.quantity);
                  ctrl.controls.cost.setValue(productPack.cost);
                  ctrl.controls.taxable.setValue(productPack.taxable);

                  const currentLabor = R.find(labourTypes, l => l.id == item.labourType);
                  const laborItems =
                    item.labourItems.length == 0
                      ? ([
                          {
                            hour: item.labour,
                            cost: item.labourCost,
                            price: item.labourPrice,
                            labourTypeId: item.labourType,
                            labourType: currentLabor as LabourTypeResponse,
                          },
                        ] as LabourItemResponse[])
                      : item.labourItems;

                  ctrl.controls.labourPrice.setValue(ProposalService.getLineLaborPrice(laborItems));
                  ctrl.controls.labourCost.setValue(ProposalService.getLineLaborCost(laborItems));
                  ctrl.controls.labour.setValue(ProposalService.getLineHours(laborItems));
                  ctrl.controls.labourItems.setValue(laborItems);
                  ctrl.controls.productLabourItems.setValue(laborItems);
                  if (productPack && productPack.description) {
                    ctrl.controls.description.setValue(CommonService.stripHtml(productPack.description));
                  }
                  if (productPack && !productPack.description) {
                    ctrl.controls.description.setValue(productPack.title);
                  }
                  if (laborItems.length == 1) {
                    ctrl.controls.labourType.setValue(laborItems[0]?.labourTypeId);
                    //   updateLineItemValue(index, laborItems, filteredItems);
                  } else if (laborItems.length > 1) {
                    ctrl.controls.labourType.setValue('0');
                  } else {
                    ctrl.controls.labourType.setValue(undefined);
                  }

                  if (productPack.description) {
                    ctrl.controls.description.setValue(CommonService.stripHtml(productPack.description));
                  }
                  if (!productPack.description) {
                    ctrl.controls.description.setValue(productPack.title);
                  }
                  if (productPack.vendorId) {
                    ctrl.controls.vendorId.setValue(productPack.vendorId);
                  }

                  totalProducts += 1;
                  totalTaxablePros += productPack.taxable ? 1 : 0;

                  setSelectedProducts([...selectedProducts, productPack]);
                }
                if (servicePack) {
                  ctrl.controls.lineTitle.setValue(servicePack.title);
                  !isTaxExempt && !ctrl.controls.taxable.value && ctrl.controls.taxable.setValue(servicePack.taxable);
                  ctrl.controls.classId.setValue(servicePack.classId);
                }
                if (servicePack && servicePack.rate) {
                  ctrl.controls.rate.setValue(servicePack.rate);
                }
                if (servicePack && servicePack.description && !R.isEmpty(servicePack.description)) {
                  ctrl.controls.description.setValue(CommonService.stripHtml(servicePack.description));
                } else if (servicePack) {
                  ctrl.controls.description.setValue(servicePack.title);
                }
              });
              inForm.controls.productImage.setValue(packageItem.image);
              inForm.controls.labour.setValue(totalLaborHours);
              inForm.controls.labourPrice.setValue(totalLaborPrice);
              inForm.controls.lineTitle.setValue(packageItem.title);
              inForm.controls.rate.setValue(packageItem.lineItems.reduce((acc, item) => acc + R.roundDecimal(item.rate, 2), 0));
              inForm.controls.sectionId.setValue(sectionId);
              inForm.controls.taxable.setValue(totalProducts == totalTaxablePros);
              if (packageItem.description && !R.isEmpty(packageItem.description)) {
                inForm.controls.description.setValue(CommonService.stripHtml(packageItem.description));
              } else {
                inForm.controls.description.setValue(packageItem.title);
              }
            }

            if (service) {
              inForm.controls.lineTitle.setValue(service.title);
              !isTaxExempt && !inForm.controls.taxable.value && inForm.controls.taxable.setValue(service.taxable);
              inForm.controls.classId.setValue(service.classId);

              const currentLabor = R.find(labourTypes, l => l.name == service.title);
              if (currentLabor) {
                inForm.controls.isServicePhase.setValue(true);
                inForm.controls.labourType.setValue(currentLabor.id);
                inForm.controls.labourPrice.setValue(currentLabor.price);
                inForm.controls.labourCost.setValue(currentLabor.cost);
                inForm.controls.labour.setValue(0);
                const lbrItems = [
                  {
                    labourTypeId: currentLabor.id,
                    hour: 0,
                    price: currentLabor.price,
                    cost: currentLabor.cost,
                    labourType: currentLabor,
                  },
                ];
                inForm.controls.labourItems.setValue(lbrItems);
                inForm.controls.productLabourItems.setValue(lbrItems);
                inForm.controls.labourItems.setValue([
                  {
                    labourTypeId: currentLabor.id,
                    hour: 0,
                    price: currentLabor.price,
                    cost: currentLabor.cost,
                  },
                ]);
              }
            }
            if (service && service.rate) {
              inForm.controls.rate.setValue(service.rate);
            }
            if (service && service.description && !R.isEmpty(service.description)) {
              inForm.controls.description.setValue(CommonService.stripHtml(service.description));
            } else if (service) {
              inForm.controls.description.setValue(service.title);
            }
          }}
          initalValue={initialValue}
          callback={search => bloc.searchProducts(search, [], true)}
        />
      </TableColumn>
      <TableColumn style={{ marginLeft: 15 }}>
        <FormSelect
          disabled={isPackage}
          items={lineTierItems}
          onChange={tierId => {
            const proId = R.replace(inForm.controls.product.value || '0', 'product-', '');
            const rate = inForm.controls.rate.value;
            const product = R.find(selectedProducts, s => s.id == proId);
            const price = R.find(product?.prices || [], s => s.tierId === tierId);
            if (price) {
              inForm.controls.rate.setValue(price.price);
            } else {
              inForm.controls.rate.setValue(rate);
            }
          }}
          control={inForm.controls.tierId}
        />
      </TableColumn>
      <TableColumn flex={2}>
        <FormInput control={inForm.controls.description} disabled={isPackage} />
      </TableColumn>
      <TableColumn>
        {isPackage ? (
          <div className="form-group">
            <Input disabled placeholder="Quantity" value={totalQty}></Input>{' '}
          </div>
        ) : (
          <FormInput control={inForm.controls.quantity} disabled={inFormValue.isServicePhase} />
        )}
        {/* <FormInput control={inForm.controls.quantity} parseToFloat /> */}
      </TableColumn>
      <TableColumn>
        {(R.isEmpty(lineUM) || lineUM == '') && !isPackage ? (
          <FormSelect items={umItems} control={inForm.controls.unitOfMeasure} disabled={inFormValue.isServicePhase} />
        ) : !isPackage ? (
          <span className="ml-1">{inForm.controls.unitOfMeasure.value}</span>
        ) : (
          ''
        )}
      </TableColumn>
      <TableColumn>
        {isPackage ? (
          <div className="form-group">
            <Input disabled placeholder="Rate" value={totalRate}></Input>
          </div>
        ) : (
          <FormInput control={inForm.controls.rate} disabled={inFormValue.isServicePhase} />
        )}
        {/* <FormInput control={inForm.controls.rate} parseToFloat /> */}
      </TableColumn>
      {!hideLaborItems && (
        <>
          <TableColumn flex={1.5} style={{ marginLeft: 15, display: 'flex', alignItems: 'center' }}>
            <FormSelect
              formGroupStyle={{ flex: 1 }}
              items={labourTypeItems}
              disabled={isPackage}
              control={inForm.controls.labourType}
              isClearable
              onChange={e => selectLabourPrice(e, index, parentIndex)}
            />
            {inForm.controls.labourType.value == '0' && (
              <a
                style={{ marginLeft: 5, marginBottom: '1rem' }}
                onClick={() =>
                  openViewLabourTypeModal({
                    typeItems: inForm.controls.labourItems.value,
                    labourTypes: labourTypes,
                    onReceive: (items: LabourItemResponse[]) => {
                      updateLineItemValue(index, items);
                    },
                  })
                }
              >
                <i className="mdi mdi-eye font-size-16 text-dark" style={{ marginTop: -4 }}></i>
              </a>
            )}
          </TableColumn>
          <TableColumn flex={0.75}>
            {isPackage ? (
              <div className="form-group">
                <Input readOnly placeholder="Labor Hour" value={totalLaborHours}></Input>
              </div>
            ) : inForm.controls.labourType.value == '0' ? (
              <FormInput control={inForm.controls.labour} parseToFloat readonly />
            ) : inForm.controls.labourItems.items.length == 1 ? (
              <FormInput control={inForm.controls.labourItems.items[0].controls.hour} parseToFloat />
            ) : (
              <FormInput control={inForm.controls.labour} parseToFloat />
            )}
          </TableColumn>
          <TableColumn flex={0.75} style={{ marginRight: 15 }}>
            {isPackage ? (
              <div className="form-group">
                <Input readOnly placeholder="Labor Price" value={totalLaborPrice}></Input>
              </div>
            ) : inForm.controls.labourType.value == '0' ? (
              <FormInput control={inForm.controls.labourPrice} parseToFloat readonly />
            ) : inForm.controls.labourItems.items.length == 1 ? (
              <FormInput control={inForm.controls.labourItems.items[0].controls.price} parseToFloat />
            ) : (
              <FormInput control={inForm.controls.labourPrice} parseToFloat />
            )}
          </TableColumn>
        </>
      )}

      {isSalesTax && (
        <TableColumn flex={0.5}>
          <FormCheckbox control={inForm.controls.taxable} disabled={isPackage} />
        </TableColumn>
      )}
      {isClasses && (
        <TableColumn>
          <FormSelect items={classItems} control={inForm.controls.classId} disabled={isPackage} />
        </TableColumn>
      )}
      <TableColumn>
        {(!R.isEmpty(lineVendorId) && R.find(vendorItems, i => i.value === inForm.controls.vendorId.value)?.label) || (
          <FormSelect isClearable control={inForm.controls.vendorId} items={vendorItems} disabled={isPackage} />
        )}
      </TableColumn>
      <TableColumn>
        <div className="form-group">
          <Input readOnly placeholder="Amount" value={getLineTotal}></Input>
        </div>
      </TableColumn>
      <TableColumn flex={0.5}>
        {index === filteredItems.length - 1 ? (
          <div className="form-group d-flex">
            {(parentIndex > -1
              ? form.controls.lineItems.items[indexToModify] && form.controls.lineItems.items[indexToModify].controls.packageItems.items.length
              : form.controls.lineItems.items.length) > 1 && (
              <Button
                type="button"
                onClick={() => {
                  const indexToRemove = lineItemsAll.findIndex(c => c.uid === inForm.uid);
                  if (parentIndex > -1) {
                    form.controls.lineItems.items[indexToModify].controls.packageItems.removeAt(index);
                  } else form.controls.lineItems.removeAt(indexToRemove);
                }}
                intent="danger"
                className="inner"
                style={{ flex: 1, marginRight: 2 }}
              >
                <i className="bx bx-trash"></i>
              </Button>
            )}
            <Button
              type="button"
              onClick={() => {
                if (parentIndex > -1) {
                  const ctrl = form.controls.lineItems.items[indexToModify].controls.packageItems.push();
                  ctrl.patchValue({ sectionId });
                } else {
                  const ctrl = form.controls.lineItems.push();
                  ctrl.patchValue({ sectionId });
                }
              }}
              intent="success"
              className="inner"
              style={{ flex: 1, marginLeft: 2 }}
            >
              <i className="bx bx-plus"></i>
            </Button>
          </div>
        ) : (
          <div className="form-group">
            <Button
              type="button"
              onClick={() => {
                if (parentIndex > -1) {
                  form.controls.lineItems.items[indexToModify].controls.packageItems.removeAt(index);
                } else {
                  const indexToRemove = lineItemsAll.findIndex(c => c.uid === inForm.uid);
                  form.controls.lineItems.removeAt(indexToRemove);
                }
              }}
              intent="danger"
              className="btn-block inner"
              style={{ width: '100%' }}
            >
              <i className="bx bx-trash"></i>
            </Button>
          </div>
        )}
      </TableColumn>
    </div>
  );
});

export interface ColapsableLineStepProps {
  form: OrderFormType;
  inForm: LineItemFormType;
  packages: OrderPackageResponse[];
  products: OrderProductResponse[];
  services: ProjectServiceResponse[];
  bloc: AddEditTimeMaterialPageBloc;
  defaultTierId?: string;
  isTaxExempt?: boolean;
  labourTypes: LabourTypeResponse[];
  sectionId: number;
  hideLaborItems?: boolean;
  tiers: PricingTierResponse[];
  selectedProducts: OrderProductResponse[];
  getLineLaborPrice: (items: LabourItemResponse[]) => number;
  getLineLaborCost: (items: LabourItemResponse[]) => number;
  getLineHours: (items: LabourItemResponse[]) => number;
  index: number;
  updateLineItemValue: (index: number, items: LabourItemResponse[], parentIndex?: number) => void;
  setSelectedProducts: (products: OrderProductResponse[]) => void;
  selectLabourPrice: (id: string, index: number, parentIndex?: number) => void;
  vendors: OrderVendorResponse[];
  classes: ServiceClassResponse[];
  isPackage?: boolean;
  parentIndex?: number;
  uid: string;
  filteredItems: LineItemFormType[];
}

export const ColapsableLineStep = memo((props: ColapsableLineStepProps) => {
  const {
    filteredItems,
    uid,
    form,
    inForm,
    packages,
    products,
    tiers,
    services,
    bloc,
    defaultTierId,
    isTaxExempt,
    labourTypes,
    sectionId,
    hideLaborItems,
    selectedProducts,
    getLineLaborPrice,
    getLineLaborCost,
    getLineHours,
    index,
    updateLineItemValue,
    setSelectedProducts,
    selectLabourPrice,
    vendors,
    classes,
    isPackage = false,
    parentIndex = -1,
  } = props;
  const [isOpen, setOpen] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const formValue = useNonNilObservable(form.value$);

  return (
    <>
      <LineStep
        form={form}
        inForm={inForm}
        key={inForm.uid}
        tiers={tiers}
        packages={packages}
        products={products}
        services={services}
        bloc={bloc}
        selectedProducts={selectedProducts}
        defaultTierId={defaultTierId}
        isTaxExempt={isTaxExempt}
        labourTypes={labourTypes}
        sectionId={sectionId}
        hideLaborItems={hideLaborItems}
        getLineLaborPrice={getLineLaborPrice}
        getLineLaborCost={getLineLaborCost}
        getLineHours={getLineHours}
        index={index}
        updateLineItemValue={updateLineItemValue}
        setSelectedProducts={setSelectedProducts}
        selectLabourPrice={selectLabourPrice}
        classes={classes}
        vendors={vendors}
        isPackage={inForm.controls.packageItems.items.length > 0 ? true : false}
        uid={inForm.uid}
        filteredItems={filteredItems}
        onOpen={() => setOpen(!isOpen)}
        isOpen={isOpen}
      />

      <Collapse isOpen={isOpen}>
        {inForm.controls.packageItems.items.map((ctrl, i) => {
          return (
            <div className="ml-5" key={ctrl.uid}>
              <LineStep
                form={form}
                inForm={ctrl}
                key={inForm.uid}
                uid={inForm.uid}
                tiers={tiers}
                packages={packages}
                products={products}
                services={services}
                bloc={bloc}
                selectedProducts={selectedProducts}
                defaultTierId={defaultTierId}
                isTaxExempt={isTaxExempt}
                labourTypes={labourTypes}
                sectionId={sectionId}
                hideLaborItems={hideLaborItems}
                getLineLaborPrice={getLineLaborPrice}
                getLineLaborCost={getLineLaborCost}
                getLineHours={getLineHours}
                index={i}
                // updateLineItemValue={updateLineItemValue}
                updateLineItemValue={(i, itemss, filItems) => updateLineItemValue(i, itemss, index)}
                setSelectedProducts={setSelectedProducts}
                selectLabourPrice={selectLabourPrice}
                classes={classes}
                vendors={vendors}
                parentIndex={index}
                filteredItems={inForm.controls.packageItems.items as LineItemFormType[]}
              />
            </div>
          );
        })}
      </Collapse>
    </>
  );
});
